import React from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { CheckCircle2, ExternalLink, ArrowRight, GraduationCap, School, Users, Accessibility } from 'lucide-react';

const OFFICIAL = 'https://e-assr.education-securite-routiere.fr/preparer/assr';

const CARDS = [
  {
    id: 'assr1',
    kicker: 'Collège · 5ᵉ (ou 14 ans)',
    title: 'ASSR 1',
    sub: 'Attestation Scolaire de Sécurité Routière — niveau 1',
    color: 'sky',
    Icon: School,
    evalue: [
      'Le piéton : traverser en sécurité',
      'Le passager : ceinture, siège auto, comportement',
      'Le cycliste : équipement, priorités, signalisation',
      'Les premiers secours (PAS : Protéger, Alerter, Secourir)',
    ],
    lien: { label: 'Lien avec le permis AM (ex-BSR)', text: 'Pour conduire un scooter 50 cm³ dès 14 ans, il faut l\'ASSR1 + une formation pratique de 8 h en moto-école.', to: null },
    train: `${OFFICIAL}/1/ASSR1`,
  },
  {
    id: 'assr2',
    kicker: 'Collège · 3ᵉ (ou 16 ans)',
    title: 'ASSR 2',
    sub: 'Attestation Scolaire de Sécurité Routière — niveau 2',
    color: 'emerald',
    Icon: GraduationCap,
    evalue: [
      'Le partage de la route (piétons, vélos, motos, voitures)',
      'Les règles de priorité et la signalisation',
      'L\'alcool, les stupéfiants, la fatigue au volant',
      'La responsabilité du conducteur (civile et pénale)',
      'L\'assurance et le comportement face à un accident',
    ],
    lien: { label: 'Lien avec le permis B', text: 'L\'ASSR2 (ou l\'ASR) est obligatoire pour s\'inscrire au permis B et à la conduite accompagnée (AAC). Sans elle, impossible de commencer.', to: '/formation/conduite-accompagnee-aac', toLabel: 'Découvrir l\'AAC' },
    train: `${OFFICIAL}/2/ASSR2`,
  },
  {
    id: 'asr',
    kicker: 'Adultes · à partir de 16 ans',
    title: 'ASR',
    sub: 'Attestation de Sécurité Routière',
    color: 'red',
    Icon: Users,
    evalue: [
      'Pour ceux qui n\'ont pas obtenu l\'ASSR2 au collège',
      'Passée en GRETA (organisme de formation continue)',
      'Mêmes thématiques que l\'ASSR2, adaptées aux adultes',
      'Équivalente à l\'ASSR2 pour l\'inscription au permis B',
    ],
    lien: { label: 'Bon à savoir', text: 'Née à l\'étranger ou déscolarisée ? L\'ASR est ta porte d\'entrée vers le permis : renseigne-toi auprès du GRETA le plus proche.', to: '/contact', toLabel: 'Poser ta question' },
    train: OFFICIAL,
  },
  {
    id: 'aer',
    kicker: 'Établissements spécialisés',
    title: 'AER',
    sub: 'Attestation d\'Éducation à la Route',
    color: 'amber',
    Icon: Accessibility,
    evalue: [
      'Pour les élèves en situation de handicap',
      'Passée en établissement spécialisé (IME, EREA…)',
      'Adaptée aux capacités de chaque candidat',
      'Ouvre les mêmes droits que l\'ASSR selon le niveau',
    ],
    lien: { label: 'Accompagnement', text: 'L\'AER est délivrée par l\'établissement : parle-en à l\'équipe pédagogique qui adapte l\'épreuve.', to: null },
    train: OFFICIAL,
  },
];

const COLORS = {
  sky: { border: 'border-sky-500/30', text: 'text-sky-400', bg: 'bg-sky-500/10', btn: 'bg-sky-600 hover:bg-sky-500' },
  emerald: { border: 'border-emerald-500/30', text: 'text-emerald-400', bg: 'bg-emerald-500/10', btn: 'bg-emerald-600 hover:bg-emerald-500' },
  red: { border: 'border-red-500/30', text: 'text-red-400', bg: 'bg-red-500/10', btn: 'bg-red-600 hover:bg-red-500' },
  amber: { border: 'border-amber-500/30', text: 'text-amber-400', bg: 'bg-amber-500/10', btn: 'bg-amber-600 hover:bg-amber-500' },
};

export default function AssrHubPage() {
  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 pt-10 sm:pt-14 pb-16" data-testid="assr-hub-page">
        <SectionTitle
          kicker="Jeunes & scolarité · Hub ASSR"
          title="ASSR, ASR, AER : par où commencer ?"
          sub="Les attestations de sécurité routière sont les premières marches vers le permis. On t'explique chacune, son rôle dans ton parcours, et où t'entraîner gratuitement sur la plateforme officielle de l'Éducation nationale."
        />

        <div className="grid md:grid-cols-2 gap-5">
          {CARDS.map((c) => {
            const col = COLORS[c.color];
            const Icon = c.Icon;
            return (
              <article key={c.id} className={`rounded-2xl border ${col.border} bg-[#0d0d0e] p-6 flex flex-col`} data-testid={`assr-card-${c.id}`}>
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl border ${col.border} ${col.bg} ${col.text} flex items-center justify-center shrink-0`}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className={`text-[10.5px] uppercase tracking-[0.22em] font-bold font-mono-spec ${col.text}`}>{c.kicker}</div>
                    <h2 className="font-serif-editorial text-white text-[30px] leading-tight">{c.title}</h2>
                    <div className="text-white/55 text-[12.5px]">{c.sub}</div>
                  </div>
                </div>
                <div className={`rounded-xl border ${col.border} bg-white/[0.02] p-4 mb-4`}>
                  <div className={`text-[10.5px] uppercase tracking-widest font-bold font-mono-spec ${col.text} mb-2.5`}>Ce qui est évalué</div>
                  <ul className="space-y-2">
                    {c.evalue.map((e, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] leading-snug">
                        <CheckCircle2 size={14} className={`${col.text} shrink-0 mt-0.5`} /> {e}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`rounded-xl border-l-2 ${col.border} bg-white/[0.02] p-4 mb-5 flex-1`}>
                  <div className={`text-[10.5px] uppercase tracking-widest font-bold font-mono-spec ${col.text} mb-1.5`}>{c.lien.label}</div>
                  <p className="text-white/70 text-[13px] leading-relaxed">{c.lien.text}</p>
                  {c.lien.to && (
                    <Link to={c.lien.to} className={`mt-2 inline-flex items-center gap-1.5 ${col.text} text-[12.5px] font-semibold hover:underline`}>
                      {c.lien.toLabel} <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
                <a
                  href={c.train}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`assr-train-${c.id}`}
                  className={`h-12 rounded-full ${col.btn} text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 transition shadow-lg`}
                >
                  S&apos;entraîner — site officiel <ExternalLink size={14} />
                </a>
                <div className="text-center text-white/35 text-[11px] font-mono-spec mt-2">e-assr.education-securite-routiere.fr — Ministère de l&apos;Éducation nationale</div>
              </article>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-serif-editorial text-white text-[22px]">Et après ton attestation ?</h3>
            <p className="text-white/60 text-[13.5px] mt-1">ASSR2 en poche, tu peux préparer le permis B dès 15 ans avec la conduite accompagnée — et réviser gratuitement ici.</p>
          </div>
          <Link to="/eleve" data-testid="assr-cta-parcours" className="h-11 px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold inline-flex items-center gap-2 shrink-0">
            Commencer mon parcours <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
